(() => {
  const container = document.getElementById('lungs-app');
  if (!container) return;

  const lungsObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) return;
      lungsObserver.disconnect();
      initLungsScene();
    },
    { threshold: 0.3 },
  );

  lungsObserver.observe(container);

  function initLungsScene() {
    container.innerHTML = '';

    const lungsGlb = document.querySelector('.lungs__glb--lungs');
    const orbsGlb = document.querySelector('.lungs__glb--orbs');

    const MODEL_A_URL = lungsGlb.dataset.image;
    const MODEL_B_URL = orbsGlb.dataset.image;

    const isMobile = window.matchMedia('(pointer:coarse)').matches;
    const DPR = Math.min(window.devicePixelRatio || 1, isMobile ? 1.25 : 1.5);
    const CAM = isMobile ? 50 : 55;

    const COUNT_A = isMobile ? 8000 : 16000;
    const COUNT_B = isMobile ? 1500 : 3000;

    const POINT_SIZE_A = 0.005;
    const POINT_SIZE_B = 0.005;

    const COLOR_A = '#ffffff';
    const COLOR_B = '#6FE1C4';

    const INTRO_DURATION = 4;
    const SPAWN_POINT_WORLD = new THREE.Vector3(0, 4, 0);
    const SPAWN_SCATTER = 6;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(CAM, 1, 0.1, 1000);
    camera.position.z = 3;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(DPR);
    renderer.outputEncoding = THREE.sRGBEncoding;
    container.appendChild(renderer.domElement);

    function resizeFromCSS() {
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    }

    requestAnimationFrame(resizeFromCSS);
    new ResizeObserver(resizeFromCSS).observe(container);

    const controls = new THREE.OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.enableZoom = false;
    controls.autoRotate = false;
    controls.autoRotateSpeed = 1;

    if (isMobile) {
      controls.enableRotate = false;
      controls.enableDamping = false;
    }

    const loader = new THREE.GLTFLoader();

    const clamp01 = (x) => Math.max(0, Math.min(1, x));
    const smootherstep = (x) => (x = clamp01(x)) * x * x * (x * (x * 6 - 15) + 10);
    const easeOutQuint = (x) => 1 - (1 - clamp01(x)) ** 5;

    function randomInSphere(r) {
      const v = new THREE.Vector3();
      do {
        v.set(Math.random() * 2 - 1, Math.random() * 2 - 1, Math.random() * 2 - 1);
      } while (v.lengthSq() > 1);
      return v.multiplyScalar(r);
    }

    function hash01(n) {
      const x = Math.sin(n * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    }

    function hashVec3(i) {
      const v = new THREE.Vector3(hash01(i * 3.1) * 2 - 1, hash01(i * 5.7) * 2 - 1, hash01(i * 9.2) * 2 - 1);
      return v.lengthSq() < 1e-6 ? new THREE.Vector3(1, 0, 0) : v.normalize();
    }

    function loadModelAsFlowPoints({ url, count, pointSize, colorHex }) {
      return new Promise((resolve, reject) => {
        // eslint-disable-next-line consistent-return
        loader.load(url, (gltf) => {
          let mesh = null;
          gltf.scene.traverse((o) => o.isMesh && !mesh && (mesh = o));
          if (!mesh) return reject();

          // 🔑 IMPORTANT FIX
          mesh.updateWorldMatrix(true, false);

          const geom = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;

          const sampler = new THREE.MeshSurfaceSampler(new THREE.Mesh(geom)).build();

          const target = new Float32Array(count * 3);
          const spawn = new Float32Array(count * 3);
          const positions = new Float32Array(count * 3);
          const rand = new Float32Array(count * 2);
          const side = new Float32Array(count * 3);

          const p = new THREE.Vector3();
          const box = new THREE.Box3();

          for (let i = 0; i < count; i++) {
            sampler.sample(p);
            p.applyMatrix4(mesh.matrixWorld); // 🔑 scale fix
            box.expandByPoint(p);
            target.set([p.x, p.y, p.z], i * 3);
          }

          const center = box.getCenter(new THREE.Vector3());

          for (let i = 0; i < count; i++) {
            const i3 = i * 3;

            target[i3] -= center.x;
            target[i3 + 1] -= center.y;
            target[i3 + 2] -= center.z;

            const j = randomInSphere(SPAWN_SCATTER);
            const sx = SPAWN_POINT_WORLD.x + j.x - center.x;
            const sy = SPAWN_POINT_WORLD.y + j.y - center.y;
            const sz = SPAWN_POINT_WORLD.z + j.z - center.z;

            spawn.set([sx, sy, sz], i3);
            positions.set([sx, sy, sz], i3);

            rand[i * 2] = hash01(i * 11.13) * Math.PI * 2;
            rand[i * 2 + 1] = 0.85 + hash01(i * 7.77) * 0.3;

            const hv = hashVec3(i);
            side.set([hv.x, hv.y, hv.z], i3);
          }

          const geometry = new THREE.BufferGeometry();
          geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

          const material = new THREE.PointsMaterial({
            size: pointSize,
            transparent: true,
            opacity: 0,
            color: new THREE.Color(colorHex),
          });

          const points = new THREE.Points(geometry, material);
          points.rotation.y = -Math.PI * 0.15;
          scene.add(points);

          resolve({
            points,
            posAttr: geometry.attributes.position,
            spawn,
            target,
            rand,
            side,
            introDone: false,
          });
        });
      });
    }

    let modelA;
    let modelB;

    Promise.all([
      loadModelAsFlowPoints({
        url: MODEL_A_URL,
        count: COUNT_A,
        pointSize: POINT_SIZE_A,
        colorHex: COLOR_A,
      }),
      loadModelAsFlowPoints({
        url: MODEL_B_URL,
        count: COUNT_B,
        pointSize: POINT_SIZE_B,
        colorHex: COLOR_B,
      }),
    ]).then(([a, b]) => {
      modelA = a;
      modelB = b;
      controls.target.set(0, 0, 0);
      controls.autoRotate = true;
    });

    const clock = new THREE.Clock();
    let tAccum = 0;

    function updateIntro(model, t) {
      if (!model || model.introDone) return;

      const pBase = clamp01(t / INTRO_DURATION);
      model.points.material.opacity = smootherstep(pBase);

      const pos = model.posAttr.array;

      for (let i = 0; i < model.posAttr.count; i++) {
        const i3 = i * 3;
        const p = clamp01(pBase * model.rand[i * 2 + 1]);
        const e = easeOutQuint(p);

        pos[i3] = model.spawn[i3] + (model.target[i3] - model.spawn[i3]) * e;
        pos[i3 + 1] = model.spawn[i3 + 1] + (model.target[i3 + 1] - model.spawn[i3 + 1]) * e;
        pos[i3 + 2] = model.spawn[i3 + 2] + (model.target[i3 + 2] - model.spawn[i3 + 2]) * e;
      }

      model.posAttr.needsUpdate = true;

      if (pBase >= 1) {
        model.introDone = true;
        model.points.material.opacity = 1;
      }
    }

    function animateLungs() {
      requestAnimationFrame(animateLungs);
      tAccum += clock.getDelta();
      updateIntro(modelA, tAccum);
      updateIntro(modelB, tAccum);
      controls.update();
      renderer.render(scene, camera);
      container.style.opacity = '1';
    }

    animateLungs();
  }
})();
